import { useState, useMemo } from 'react';
import { FileText, Clock, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

type InternalTab = 'actions' | 'sessions';

// ── helpers ──────────────────────────────────────────────

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

const parseBrowser = (ua: string | null): string => {
  if (!ua) return '—';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Chrome/')) return 'Chrome';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
  return ua.length > 30 ? ua.slice(0, 30) + '…' : ua;
};

const actionMeta: Record<string, { label: string; variant: string }> = {
  create: { label: 'Criação', variant: 'bg-green-600/15 text-green-600 border-green-600/30' },
  update: { label: 'Atualização', variant: 'bg-blue-600/15 text-blue-600 border-blue-600/30' },
  delete: { label: 'Deleção', variant: 'bg-red-600/15 text-red-600 border-red-600/30' },
};

const actionBadge = (action: string) => {
  const prefix = action.split('_')[0];
  const meta = actionMeta[prefix] ?? { label: action, variant: '' };
  return (
    <Badge variant="outline" className={meta.variant}>
      {meta.label}
    </Badge>
  );
};

const targetLabel = (t: string | null) => {
  if (!t) return '—';
  const map: Record<string, string> = {
    agent: 'Agente', prompt: 'Prompt', tool: 'Ferramenta', project: 'Projeto',
  };
  return map[t] ?? t;
};

// ── main component ──────────────────────────────────────

export const AuditLogsPanel = () => {
  const [tab, setTab] = useState<InternalTab>('actions');

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-secondary rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('actions')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            tab === 'actions' ? 'bg-surface text-primary shadow-sm' : 'text-secondary hover:text-primary'
          }`}
        >
          <FileText className="h-3.5 w-3.5" />
          Logs de Ações
        </button>
        <button
          onClick={() => setTab('sessions')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            tab === 'sessions' ? 'bg-surface text-primary shadow-sm' : 'text-secondary hover:text-primary'
          }`}
        >
          <Clock className="h-3.5 w-3.5" />
          Sessões de Login
        </button>
      </div>

      {tab === 'actions' ? <ActionsTab /> : <SessionsTab />}
    </div>
  );
};

// ── Actions Tab ─────────────────────────────────────────

const ActionsTab = () => {
  const [limit, setLimit] = useState(50);
  const [actionFilter, setActionFilter] = useState('all');
  const [search, setSearch] = useState('');

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['admin-audit-logs', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*, profiles:user_id(nome, email, avatar_url)')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });

  const filtered = useMemo(() => {
    let list = logs;
    if (actionFilter !== 'all') {
      list = list.filter((l: any) => l.action?.startsWith(actionFilter));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((l: any) => {
        const p = l.profiles as any;
        return p?.nome?.toLowerCase().includes(q) || p?.email?.toLowerCase().includes(q);
      });
    }
    return list;
  }, [logs, actionFilter, search]);

  if (isLoading) return <LoadingSkeleton />;

  if (logs.length === 0) {
    return (
      <EmptyState icon={FileText} text="Nenhum registro de auditoria encontrado." />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
        >
          <option value="all">Todas as ações</option>
          <option value="create">Criação</option>
          <option value="update">Atualização</option>
          <option value="delete">Deleção</option>
        </select>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
          <input
            type="text"
            placeholder="Buscar por usuário..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </div>
      </div>

      <div className="rounded-lg border border-color overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead>Data/hora</TableHead>
              <TableHead>Usuário</TableHead>
              <TableHead>Ação</TableHead>
              <TableHead>Recurso</TableHead>
              <TableHead>Detalhes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((log: any) => {
              const p = log.profiles as any;
              const displayName = p?.nome || p?.email || '—';
              return (
                <TableRow key={log.id}>
                  <TableCell className="text-sm text-secondary whitespace-nowrap">
                    {fmtDate(log.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        {p?.avatar_url && <AvatarImage src={p.avatar_url} />}
                        <AvatarFallback className="text-[9px] bg-secondary text-primary">
                          {initials(displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-primary">{displayName}</span>
                    </div>
                  </TableCell>
                  <TableCell>{actionBadge(log.action)}</TableCell>
                  <TableCell className="text-sm text-secondary">
                    {targetLabel(log.target_type)}
                  </TableCell>
                  <TableCell className="text-xs text-tertiary max-w-[200px] truncate">
                    {log.metadata && Object.keys(log.metadata).length > 0
                      ? Object.entries(log.metadata).map(([k, v]) => `${k}: ${v}`).join(', ')
                      : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-secondary py-8">
                  Nenhum log corresponde aos filtros.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {logs.length >= limit && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => setLimit((l) => l + 50)}>
            Carregar mais
          </Button>
        </div>
      )}
    </div>
  );
};

// ── Sessions Tab ────────────────────────────────────────

const SessionsTab = () => {
  const [limit, setLimit] = useState(50);

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['admin-login-sessions', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('login_sessions')
        .select('*, profiles:user_id(nome, email, avatar_url)')
        .order('logged_in_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });

  if (isLoading) return <LoadingSkeleton />;

  if (sessions.length === 0) {
    return <EmptyState icon={Clock} text="Nenhuma sessão registrada." />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-color overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead>Data/hora</TableHead>
              <TableHead>Usuário</TableHead>
              <TableHead>Navegador</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((s: any) => {
              const p = s.profiles as any;
              const displayName = p?.nome || p?.email || '—';
              return (
                <TableRow key={s.id}>
                  <TableCell className="text-sm text-secondary whitespace-nowrap">
                    {fmtDate(s.logged_in_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        {p?.avatar_url && <AvatarImage src={p.avatar_url} />}
                        <AvatarFallback className="text-[9px] bg-secondary text-primary">
                          {initials(displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm text-primary">{displayName}</span>
                        <span className="text-xs text-tertiary">{p?.email}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-secondary">
                    {parseBrowser(s.user_agent)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {sessions.length >= limit && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => setLimit((l) => l + 50)}>
            Carregar mais
          </Button>
        </div>
      )}
    </div>
  );
};

// ── shared ──────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-12 w-full" />
    ))}
  </div>
);

const EmptyState = ({ icon: Icon, text }: { icon: typeof FileText; text: string }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <Icon className="h-10 w-10 text-tertiary mb-3" />
    <p className="text-secondary text-sm">{text}</p>
  </div>
);
